import axios from 'axios'
import { SHARED_PATHS } from '@/utilities/paths'
import { getStytchSessionTokens } from '@/utilities/stytch/getStytchJWT'

/**
 * Portal API returns these `detail` strings (no portal_error_code) when a patient
 * route rejects the Stytch cookie. Treat them as a cross-repo contract until Portal API
 * adds stable `portal_error_code` values for session rejection.
 */
const MISSING_STYTCH_SESSION_DETAIL = 'User not authenticated (missing stytch_session_jwt cookie)'
const INVALID_STYTCH_SESSION_DETAIL = 'User not authenticated (invalid stytch_session_jwt cookie)'
const MISSING_INTERNAL_ORG_DETAIL =
  'Cannot identify organization (missing internal_organization_id)'
const USER_NOT_FOUND_DETAIL = 'User not found.'

const PATIENT_PROFILE_PATH = '/v2/users/me'
const IDENTITY_PATH = '/v2/auth/identity'
const USER_DISCOVERY_PATH = '/v2/auth/user-discovery'
const SIGNUP_PATHS = new Set(['/v3/auth/sign-up', '/v3/auth/sign-up-with-kit-and-hipaa-consent'])
/**
 * These routes return 403 when a new patient has a Stytch session but no Portal account yet.
 * The patient stays on the page.
 */
const NO_ACCOUNT_YET_PATHS = new Set<string>([IDENTITY_PATH, USER_DISCOVERY_PATH, ...SIGNUP_PATHS])

const EXPLICIT_END_SESSION_DETAILS = new Set([MISSING_STYTCH_SESSION_DETAIL])

/**
 * What a patient Portal API failure means for the shared session.
 * - `end-session`: the server rejected the Stytch session.
 * - `verify-session`: the failure may mean the session is gone. Confirm with Stytch first.
 * - `stay-on-page`: any other failure. The original request surfaces its error.
 */
export type PatientApiFailureAction = 'end-session' | 'verify-session' | 'stay-on-page'

export class PatientSessionExpiredError extends Error {
  constructor() {
    super('Patient session expired')
    this.name = 'PatientSessionExpiredError'
  }
}

type SessionListener = () => void

const listeners = new Set<SessionListener>()
const dataInvalidators = new Set<() => void>()

let sessionExpired = false
let verificationPendingCount = 0

function notifyPatientSessionListeners(): void {
  for (const listener of listeners) listener()
}

export function subscribePatientSession(listener: SessionListener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function isPatientSessionExpired(): boolean {
  return sessionExpired
}

/** True while a Stytch check runs for an ambiguous Portal API failure. */
export function isPatientSessionVerificationPending(): boolean {
  return verificationPendingCount > 0
}

/**
 * True while a failure may still end the session. Step UI hides submit and load errors
 * during this window so a stale-session 401 does not flash an error before the redirect.
 */
export function shouldSuppressPatientSessionFailureUi(): boolean {
  return sessionExpired || verificationPendingCount > 0
}

/** True when a Portal API failure should not surface step or load errors yet. */
export function shouldHoldPatientApiFailure(error: unknown): boolean {
  return error instanceof PatientSessionExpiredError || shouldSuppressPatientSessionFailureUi()
}

export function beginPatientSessionVerification(): void {
  verificationPendingCount += 1
  notifyPatientSessionListeners()
}

export function endPatientSessionVerification(): void {
  verificationPendingCount = Math.max(0, verificationPendingCount - 1)
  notifyPatientSessionListeners()
}

/**
 * Returns the patient login path that the root layout renders on `<html>`.
 * The value exists before any client code runs, so an early failure still uses the tenant login.
 */
export function getPatientSessionLoginRedirect(): string {
  if (typeof document === 'undefined') return SHARED_PATHS.login
  const path = document.documentElement.dataset.patientLoginRedirect?.trim()
  return path || SHARED_PATHS.login
}

export function registerPatientSessionDataInvalidator(invalidate: () => void): void {
  dataInvalidators.add(invalidate)
}

export function invalidatePatientSessionData(): void {
  for (const invalidate of dataInvalidators) {
    try {
      invalidate()
    } catch (error) {
      console.warn('Failed to invalidate patient session data:', error)
    }
  }
}

/**
 * Marks the shared patient session expired and notifies subscribers.
 * Returns false when expiration was already established.
 */
export function markPatientSessionExpired(): boolean {
  if (sessionExpired) return false
  sessionExpired = true
  notifyPatientSessionListeners()
  return true
}

export function resetPatientSessionForTests(): void {
  sessionExpired = false
  verificationPendingCount = 0
}

function requestPath(error: unknown): string {
  if (!axios.isAxiosError(error)) return ''
  const rawUrl = error.config?.url ?? ''
  if (!rawUrl) return ''

  try {
    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
      return new URL(rawUrl).pathname
    }
    const base = error.config?.baseURL
    if (base && (base.startsWith('http://') || base.startsWith('https://'))) {
      return new URL(rawUrl, base).pathname
    }
    const path = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`
    return path.split('?')[0] ?? path
  } catch {
    return rawUrl.split('?')[0] ?? ''
  }
}

function responseDetail(data: unknown): string {
  if (typeof data === 'string') return data.trim()
  if (!data || typeof data !== 'object' || !('detail' in data)) return ''
  const detail = (data as { detail?: unknown }).detail
  return typeof detail === 'string' ? detail.trim() : ''
}

function isExpectedSignupOrIdentityMiss(status: number, path: string, detail: string): boolean {
  if (status !== 404) return false
  if (path === PATIENT_PROFILE_PATH || path === IDENTITY_PATH) return true
  return SIGNUP_PATHS.has(path) && detail === USER_NOT_FOUND_DETAIL
}

/**
 * True when the browser has no Stytch session cookies.
 * The cookies are the signal. The Stytch member can remain after the cookies are deleted.
 * When `document` is missing, this returns false and the session stays in place.
 */
function isBrowserStytchSessionGone(): boolean {
  if (typeof document === 'undefined') return false
  return getStytchSessionTokens() == null
}

/**
 * Decides what a patient Portal API failure means for the shared session.
 * Permission denials, signup and identity misses, server errors, and other
 * 4xx responses stay on the page.
 * A profile 403 ends the session only when the Stytch cookies are already gone.
 * Signup, identity, and user-discovery 403s mean the patient has no Portal account yet.
 * Those responses stay on the page.
 */
export function classifyPatientApiFailure(error: unknown): PatientApiFailureAction {
  if (!axios.isAxiosError(error) || !error.response) return 'stay-on-page'

  const { status, data } = error.response
  if (status >= 500) return 'stay-on-page'

  const detail = responseDetail(data)
  const path = requestPath(error)
  if (isExpectedSignupOrIdentityMiss(status, path, detail)) return 'stay-on-page'
  if (status === 403) {
    if (NO_ACCOUNT_YET_PATHS.has(path)) return 'stay-on-page'
    if (path === PATIENT_PROFILE_PATH && isBrowserStytchSessionGone()) return 'end-session'
    return 'stay-on-page'
  }
  if (EXPLICIT_END_SESSION_DETAILS.has(detail)) return 'end-session'
  // A stale or locally invalid JWT can still have a refreshable Stytch session — confirm first.
  if (
    status === 401 ||
    detail === INVALID_STYTCH_SESSION_DETAIL ||
    detail === MISSING_INTERNAL_ORG_DETAIL
  ) {
    return 'verify-session'
  }

  return 'stay-on-page'
}
